export interface WorkoutData {
  name: string;
  exercises: { name: string; sets: { reps: number; weight: number }[] }[];
}

export interface ProgramData {
  name: string;
  workouts: WorkoutData[];
  restDays?: string[]; // e.g., ["Wednesday", "Sunday"]
}

export interface GoalsData {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  reason: string;
}

export interface ScheduleData {
  schedule: Record<string, string>; // e.g., { "monday": "Push", "tuesday": "Pull" }
  reason: string;
}

export interface ProfileData {
  weight_kg?: number;
  height_cm?: number;
  age?: number;
  gender?: string;
  goal_weight_kg?: number;
  activity_level?: string;
  fitness_goal?: string;
}

export interface LogFoodData {
  /** Natural-language description of what the user ate. Jake NEVER supplies macros. */
  description: string;
  meal_type?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
}

export interface SwapExerciseData {
  /** Exercise to remove from today's session (name as Jake said it). */
  from: string;
  /** Replacement exercise name — must resolve to a real library exercise. */
  to: string;
  /** Short reason shown on the card, e.g. "Easier on a sore shoulder". */
  reason: string;
  /** 'today' for today's session, 'date' for a future day. Never rewrites the programme. */
  scope: 'today' | 'date';
  /** Optional ISO yyyy-MM-dd target date. Omitted = today. */
  date?: string;
}


export interface ExerciseDemoData {
  exercises: { name: string; description?: string }[];
}

// Helper to find and extract content between markers using string methods (more reliable than regex)
function extractJsonBlock(content: string, blockType: string): string | null {
  const startMarker = '```' + blockType;
  
  const startIdx = content.indexOf(startMarker);
  if (startIdx === -1) return null;
  
  // Get the position after the start marker
  const afterMarker = startIdx + startMarker.length;
  
  // Find the closing ``` - need to skip any whitespace/newlines first, then find content, then find ```
  const remainingContent = content.substring(afterMarker);
  
  // Look for closing ``` that's on its own or at start of line
  // The JSON ends when we hit a line that starts with ``` or just ```
  let endIdx = -1;
  let searchPos = 0;
  
  while (searchPos < remainingContent.length) {
    const nextBackticks = remainingContent.indexOf('```', searchPos);
    if (nextBackticks === -1) break;
    
    // Check if these backticks are at start of line or preceded by newline
    if (nextBackticks === 0 || remainingContent[nextBackticks - 1] === '\n' || remainingContent[nextBackticks - 1] === '\r') {
      endIdx = nextBackticks;
      break;
    }
    searchPos = nextBackticks + 3;
  }
  
  if (endIdx === -1) return null;
  
  const jsonContent = remainingContent.substring(0, endIdx).trim();
  return jsonContent;
}

// Helper to remove all JSON blocks using string methods
function removeJsonBlocks(content: string): string {
  let result = content;
  const blockTypes = ['workout-json', 'program-json', 'goals-json', 'schedule-json', 'profile-json', 'coach-plan-json', 'exercise-demo-json', 'log-food-json', 'swap-exercise-json'];
  
  for (const type of blockTypes) {
    const startMarker = '```' + type;
    const endMarker = '```';
    
    let startIdx = result.indexOf(startMarker);
    while (startIdx !== -1) {
      const afterStart = startIdx + startMarker.length;
      const endIdx = result.indexOf(endMarker, afterStart);
      
      if (endIdx !== -1) {
        result = result.substring(0, startIdx) + result.substring(endIdx + 3);
      } else {
        result = result.substring(0, startIdx);
        break;
      }
      
      startIdx = result.indexOf(startMarker);
    }
  }
  
  return result;
}

// Parse workout JSON from message content
export function parseWorkoutFromContent(content: string): WorkoutData | null {
  const jsonStr = extractJsonBlock(content, 'workout-json');
  if (jsonStr) {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      console.error('[parseWorkoutFromContent] JSON parse error:', e);
      return null;
    }
  }
  return null;
}

// Parse program JSON from message content (multi-day splits)
export function parseProgramFromContent(content: string): ProgramData | null {
  let jsonStr = extractJsonBlock(content, 'program-json');
  
  // If no complete block found, try to extract incomplete/truncated JSON
  if (!jsonStr) {
    jsonStr = extractIncompleteJsonBlock(content, 'program-json');
  }
  
  if (jsonStr) {
    try {
      // Try to repair truncated JSON before parsing
      const repairedJson = repairTruncatedJson(jsonStr);
      const parsed = JSON.parse(repairedJson);
      
      // Validate that the parsed program has the required structure
      if (!parsed.workouts || !Array.isArray(parsed.workouts)) {
        return null;
      }
      
      // Filter out rest days from workouts array (in case AI accidentally included them)
      // Rest days are identified by name containing "rest" or having no exercises
      const filteredWorkouts = parsed.workouts.filter((workout: WorkoutData) => {
        const name = (workout.name || '').toLowerCase();
        const isRestDay = name.includes('rest') && !name.includes('chest'); // "chest" contains "rest"
        const hasNoExercises = !workout.exercises || workout.exercises.length === 0;
        return !isRestDay && !hasNoExercises;
      });
      
      // Ensure each workout has an exercises array
      for (const workout of filteredWorkouts) {
        if (!workout.exercises || !Array.isArray(workout.exercises)) {
          workout.exercises = [];
        }
      }
      
      // Enforce max 7 workouts
      const limitedWorkouts = filteredWorkouts.slice(0, 7);
      
      return {
        name: parsed.name || 'Workout Program',
        workouts: limitedWorkouts,
        restDays: parsed.restDays || [],
      };
    } catch (e) {
      return null;
    }
  }
  return null;
}

// Extract incomplete JSON block (when AI response was truncated)
function extractIncompleteJsonBlock(content: string, blockType: string): string | null {
  const startMarker = '```' + blockType;
  const startIdx = content.indexOf(startMarker);
  if (startIdx === -1) return null;
  
  const afterMarker = startIdx + startMarker.length;
  const remainingContent = content.substring(afterMarker).trim();
  
  // No closing backticks - return what we have (truncated)
  return remainingContent;
}

// Attempt to repair truncated JSON by closing open brackets/braces
function repairTruncatedJson(jsonStr: string): string {
  let repaired = jsonStr.trim();
  
  // Remove trailing incomplete content (cut-off strings, etc.)
  // Find the last complete structure
  const lastCompleteIdx = Math.max(
    repaired.lastIndexOf('}'),
    repaired.lastIndexOf(']')
  );
  
  if (lastCompleteIdx > 0) {
    repaired = repaired.substring(0, lastCompleteIdx + 1);
  }
  
  // Count open/close brackets and braces
  let openBraces = 0;
  let openBrackets = 0;
  let inString = false;
  let escapeNext = false;
  
  for (let i = 0; i < repaired.length; i++) {
    const char = repaired[i];
    
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    
    if (char === '\\') {
      escapeNext = true;
      continue;
    }
    
    if (char === '"') {
      inString = !inString;
      continue;
    }
    
    if (!inString) {
      if (char === '{') openBraces++;
      if (char === '}') openBraces--;
      if (char === '[') openBrackets++;
      if (char === ']') openBrackets--;
    }
  }
  
  // Close any unclosed structures
  while (openBrackets > 0) {
    repaired += ']';
    openBrackets--;
  }
  while (openBraces > 0) {
    repaired += '}';
    openBraces--;
  }
  
  return repaired;
}

// Parse goals JSON from message content
export function parseGoalsFromContent(content: string): GoalsData | null {
  const jsonStr = extractJsonBlock(content, 'goals-json');
  if (jsonStr) {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      return null;
    }
  }
  return null;
}

// Parse schedule JSON from message content
export function parseScheduleFromContent(content: string): ScheduleData | null {
  const jsonStr = extractJsonBlock(content, 'schedule-json');
  if (jsonStr) {
    try {
      const parsed = JSON.parse(jsonStr);
      // Support both old format (training_days array) and new format (schedule object)
      if (parsed.schedule && typeof parsed.schedule === 'object') {
        return parsed;
      }
      // Convert old format to new format
      if (parsed.training_days && Array.isArray(parsed.training_days)) {
        const schedule: Record<string, string> = {};
        parsed.training_days.forEach((day: string) => {
          schedule[day] = 'Training';
        });
        return { schedule, reason: parsed.reason || '' };
      }
      return null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

// Parse profile JSON from message content
export function parseProfileFromContent(content: string): ProfileData | null {
  const jsonStr = extractJsonBlock(content, 'profile-json');
  if (jsonStr) {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      return null;
    }
  }
  return null;
}

// Parse exercise demo JSON from message content
export function parseExerciseDemoFromContent(content: string): ExerciseDemoData | null {
  const jsonStr = extractJsonBlock(content, 'exercise-demo-json');
  if (!jsonStr) return null;
  try {
    const parsed = JSON.parse(jsonStr);
    if (!parsed.exercises || !Array.isArray(parsed.exercises)) return null;
    const exercises = parsed.exercises
      .filter((e: any) => e && typeof e.name === 'string' && e.name.trim().length > 0)
      .map((e: any) => ({
        name: String(e.name).trim(),
        description: typeof e.description === 'string' ? e.description.trim() : undefined,
      }));
    if (exercises.length === 0) return null;
    return { exercises };
  } catch {
    return null;
  }
}

// Parse a food-logging request from the coach. Jake only supplies the natural
// language description + meal slot; macros come from the estimate-food edge fn.
export function parseLogFoodFromContent(content: string): LogFoodData | null {
  const jsonStr = extractJsonBlock(content, 'log-food-json');
  if (!jsonStr) return null;
  try {
    const parsed = JSON.parse(jsonStr);
    const description = typeof parsed?.description === 'string' ? parsed.description.trim() : '';
    if (!description) return null;
    const slot = typeof parsed?.meal_type === 'string' ? parsed.meal_type.toLowerCase().trim() : '';
    const meal_type = ['breakfast', 'lunch', 'dinner', 'snack'].includes(slot)
      ? (slot as LogFoodData['meal_type'])
      : undefined;
    return { description, meal_type };
  } catch {
    return null;
  }
}

// Parse a today-only exercise swap proposed by the coach (sore joint, missing
// kit). Jake names the replacement; the client resolves it against the library.
export function parseSwapExerciseFromContent(content: string): SwapExerciseData | null {
  const jsonStr = extractJsonBlock(content, 'swap-exercise-json');
  if (!jsonStr) return null;
  try {
    const parsed = JSON.parse(jsonStr);
    const from = typeof parsed?.from === 'string' ? parsed.from.trim() : '';
    const to = typeof parsed?.to === 'string' ? parsed.to.trim() : '';
    if (!from || !to) return null;
    const reason = typeof parsed?.reason === 'string' ? parsed.reason.trim() : '';
    const rawDate = typeof parsed?.date === 'string' ? parsed.date.trim() : '';
    const date = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : undefined;
    return { from, to, reason, scope: date ? 'date' : 'today', date };

  } catch {
    return null;
  }
}

// Parse a request from the coach to (re)build the user's autonomous coaching
// programme based on a free-form description. Coach emits this when the user
// asks them to build/change/replace their multi-week plan in chat.
export interface CoachPlanRequest {
  goal_description: string;
  duration_weeks?: number;
}

export function parseCoachPlanRequest(content: string): CoachPlanRequest | null {
  const jsonStr = extractJsonBlock(content, 'coach-plan-json');
  if (!jsonStr) return null;
  try {
    const parsed = JSON.parse(jsonStr);
    const desc = typeof parsed?.goal_description === 'string' ? parsed.goal_description.trim() : '';
    if (!desc) return null;
    const out: CoachPlanRequest = { goal_description: desc };
    if (typeof parsed?.duration_weeks === 'number' && parsed.duration_weeks > 0) {
      out.duration_weeks = parsed.duration_weeks;
    }
    return out;
  } catch {
    return null;
  }
}

// Get display content (without the JSON blocks)
export function getDisplayContent(content: string): string {
  // First try string-based removal (most reliable)
  let result = removeJsonBlocks(content);
  
  // Fallback: also try regex in case of different encoding
  result = result
    .replace(/```workout-json[\s\S]*?```/g, '')
    .replace(/```program-json[\s\S]*?```/g, '')
    .replace(/```goals-json[\s\S]*?```/g, '')
    .replace(/```schedule-json[\s\S]*?```/g, '')
    .replace(/```profile-json[\s\S]*?```/g, '')
    .replace(/```coach-plan-json[\s\S]*?```/g, '')
    .replace(/```exercise-demo-json[\s\S]*?```/g, '')
    .replace(/```log-food-json[\s\S]*?```/g, '')
    .replace(/```swap-exercise-json[\s\S]*?```/g, '')
    // Strip generic ```json ...``` fences that occasionally leak through
    .replace(/```json[\s\S]*?```/gi, '')
    // Defensive: strip raw (unfenced) JSON objects that contain known
    // structured keys. The model is instructed to wrap these in fenced
    // blocks, but occasionally leaks raw JSON into the prose.
    .replace(/\{[\s\S]*?"(?:exercises|workouts|calories|protein|trainingDays|weight_kg)"[\s\S]*?\}/g, '');

  // Humanize: strip em/en dashes and double hyphens from coach text.
  // Note: the dot-collapse regex requires whitespace BETWEEN the two dots so
  // that ellipses ("...") are preserved verbatim. Without this guard, "..."
  // gets chewed down to ".." during streaming display.
  result = result.replace(/—|–|--/g, '. ').replace(/\.\s+\.\s*/g, '. ').replace(/\s+\./g, '.');

  return result.trim();
}

// Get display content for streaming - hides incomplete JSON blocks during generation
export function getStreamingDisplayContent(content: string): string {
  // First remove complete blocks
  let result = getDisplayContent(content);
  
  // Check for unclosed blocks being typed
  const blockTypes = ['workout-json', 'program-json', 'goals-json', 'schedule-json', 'profile-json', 'exercise-demo-json', 'log-food-json', 'swap-exercise-json'];
  for (const type of blockTypes) {
    const marker = '```' + type;
    const idx = result.indexOf(marker);
    if (idx !== -1) {
      // Check if there's a closing ``` after it
      const afterMarker = idx + marker.length;
      const closeIdx = result.indexOf('```', afterMarker);
      if (closeIdx === -1) {
        // Unclosed block - remove from marker to end
        result = result.substring(0, idx);
        break;
      }
    }
  }
  
  // Also catch partial backticks at the end
  result = result.replace(/`{1,3}(workout|program|goals|schedule|profile|exercise-demo|log-food|swap-exercise|w|p|g|s|e|l)?[-]?(json)?[\s\S]*$/i, '');
  
  return result.trim();
}

// Check if content contains an incomplete JSON block being generated
export function isGeneratingStructuredContent(content: string): boolean {
  const blockTypes = ['workout-json', 'program-json', 'goals-json', 'schedule-json', 'profile-json', 'exercise-demo-json', 'log-food-json', 'swap-exercise-json'];
  
  for (const type of blockTypes) {
    const marker = '```' + type;
    const idx = content.indexOf(marker);
    if (idx !== -1) {
      const afterMarker = idx + marker.length;
      const closeIdx = content.indexOf('```', afterMarker);
      if (closeIdx === -1) {
        return true; // Unclosed block
      }
    }
  }
  
  // Check for partial patterns being typed
  if (/`{1,3}(workout|program|goals|schedule|profile|exercise-demo|log-food|swap-exercise)[-]?(json)?$/i.test(content)) {
    return true;
  }
  
  return false;
}

// Stabilize partial markdown so streaming text doesn't reflow when closing
// markers arrive. Strips the trailing incomplete link/code, then closes any
// unmatched inline-pair markers (**, __, *, _) so they render in their final
// visual style even before the model emits the closing marker.
//
// Idempotent for fully-formed input: balanced text passes through unchanged,
// so the bubble looks identical before and after `isStreaming` flips to false.
export function stabilizePartialMarkdown(text: string): string {
  if (!text) return text;
  let out = text;

  // 1. Drop an unfinished link/image at the very end: `[label`, `[label](`,
  //    `[label](http`. Once the closing `)` arrives we re-render as a link.
  out = out.replace(/!?\[[^\]]*\]\([^)]*$/, '');
  out = out.replace(/!?\[[^\]]*$/, '');

  // 2. Drop trailing dangling inline-code openers that aren't yet closed.
  //    If backtick count is odd, strip from the last backtick to the end so
  //    we don't flash raw backticks mid-stream.
  const tickCount = (out.match(/`/g) || []).length;
  if (tickCount % 2 === 1) {
    const lastTick = out.lastIndexOf('`');
    if (lastTick !== -1) out = out.slice(0, lastTick);
  }

  // 3. Balance bold (`**`, `__`). If pair count is odd, append a closing pair
  //    so the in-flight token renders bold immediately.
  const closePair = (s: string, marker: string) => {
    const esc = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const count = (s.match(new RegExp(esc, 'g')) || []).length;
    return count % 2 === 1 ? s + marker : s;
  };
  out = closePair(out, '**');
  out = closePair(out, '__');

  // 4. Balance italic (`*`, `_`). Only count standalone markers (not part of
  //    a `**`/`__` pair). If odd, append a closer.
  //
  //    A leading `* ` is a list bullet, not an italic opener — counting it
  //    would append a stray closing `*` to the end of the line.
  const isListMarker = (s: string, i: number) => {
    if (s[i] !== '*') return false;
    const next = s[i + 1];
    if (next !== ' ' && next !== '\t') return false;
    for (let j = i - 1; j >= 0; j--) {
      const c = s[j];
      if (c === '\n') return true;
      if (c !== ' ' && c !== '\t') return false;
    }
    return true; // reached start of string through whitespace only
  };
  const balanceSingle = (s: string, ch: string) => {
    let count = 0;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === ch && s[i - 1] !== ch && s[i + 1] !== ch && !isListMarker(s, i)) count++;
    }
    return count % 2 === 1 ? s + ch : s;
  };
  out = balanceSingle(out, '*');
  out = balanceSingle(out, '_');

  return out;
}

// Simple markdown parser for chat messages. Stream-stable: partial input is
// stabilized before substitution so styles don't flip when closing markers
// finally arrive.
export function parseMarkdown(text: string): string {
  return stabilizePartialMarkdown(text)
    // Bold: **text** or __text__
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    // Lists must run before italic: a leading `* item` would otherwise be
    // consumed by the `*text*` italic rule and render as <em> instead of <li>.
    // Lists: - item or * item (leading indent allowed for nested bullets)
    .replace(/^[ \t]*[-*]\s+(.+)$/gm, '<li class="ml-4">$1</li>')
    // Numbered lists: 1. item
    .replace(/^[ \t]*\d+\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
    // Italic: *text* or _text_
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Inline code: `code`
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-xs font-mono">$1</code>')
    // Links: [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary underline">$1</a>');
}
